import { createFeatureSelector, createSelector, MemoizedSelector } from '@ngrx/store';
import { ADDRESS_FEATURE_KEY, AddressCollectionState, AddressState } from '../address-collection.state';
import { addressesById } from '../address-lookup';

export const selectAddressFeature = createFeatureSelector<AddressCollectionState>(ADDRESS_FEATURE_KEY);

// --- Raw serializable slices -------------------------------------------------

export const selectAddresses = createSelector(selectAddressFeature, (state) => state.addresses);

/**
 * The addresses the pickers may offer: visible, not tombstoned, and in scope.
 *
 * This is the single choke point pickers read (`selectTimesheetAddressChoices` reads it, not
 * `selectAddresses`), so it is also the single place that keeps a deleted or out-of-scope address
 * out of them. A timesheet entry stores only an address id, though, and must keep resolving one even
 * after the address book deletes it, or the backend stops sending it — for an already-recorded
 * entry, and for the legacy model bridge and submit out-model of an already-submitted timesheet —
 * so `selectAddresses` / `selectAddressesById` deliberately keep every address, tombstoned,
 * out-of-scope, or neither.
 */
export const selectVisibleAddresses = createSelector(selectAddresses, (state) =>
    state.filter((a) => a.visible && !a.deleted && !a.outOfScope),
);

/** The address collection indexed by id, for resolving a timesheet entry's stored address id. */
export const selectAddressesById: MemoizedSelector<object, ReadonlyMap<string, AddressState>> = createSelector(
    selectAddresses,
    addressesById,
);

/**
 * Whether any address is currently hidden, and so whether restoring them would do anything.
 *
 * Computed over *all* addresses rather than the visible ones. The legacy
 * `AddressSelectComponent.getVisibleAddresses` filtered to the visible addresses first and then
 * asked whether they were all visible, which is trivially true — so its reset button was
 * permanently disabled.
 *
 * A tombstoned address is excluded from the count even when it is also hidden: `resetAddressVisibility`
 * cannot undelete it (un-hiding is not un-deleting — see the reducer), so counting it would light up
 * the restore button for a click that does nothing. An out-of-scope address is excluded for the same
 * reason: `resetAddressVisibility` cannot bring back an address the backend is not currently
 * offering either (see `AddressState.outOfScope`), so counting it would light up the restore button
 * for a click that does nothing here too.
 */
export const selectHasHiddenAddresses = createSelector(selectAddresses, (addresses) =>
    addresses.some((address) => !address.visible && !address.deleted && !address.outOfScope),
);
export const selectAddressStatus = createSelector(selectAddressFeature, (state) => state.status);
export const selectAddressError = createSelector(selectAddressFeature, (state) => state.error);
